import { Box } from '@mantine/core';
import React, { cloneElement, isValidElement, useRef } from 'react';
import type { ReactNode } from 'react';

import { useResizableColumns } from '@ayocore/platform/client/hooks/useResizableColumns';

import { SplitPane } from './SplitPane.tsx';
import { SplitResizer } from './SplitResizer.tsx';
import type { SplitPaneHandle } from './SplitPane.tsx';

export interface SplitProps {
  storageKey: string;
  defaultLeftPercent?: number;
  minPercent?: number;
  maxPercent?: number;
  className?: string;
  style?: React.CSSProperties;
  children?: ReactNode;
}

/**
 * Декларативный контейнер для двух колонок с ресайзом (подход как у @gfazioli/mantine-split-pane).
 * Layout: flex; панели получают ref и initialWidth/minWidth/maxWidth; ресайзер выставляет style.width обеим панелям.
 * Ожидает детей: Split.Pane, Split.Resizer, Split.Pane.
 */
export function Split({
  storageKey,
  defaultLeftPercent = 65,
  minPercent = 20,
  maxPercent = 80,
  className,
  style,
  children,
}: SplitProps) {
  const { initialLeftPercent, saveLeftPercent } = useResizableColumns({
    storageKey,
    defaultLeftPercent,
    minPercent,
    maxPercent,
  });

  const paneBeforeRef = useRef<SplitPaneHandle | null>(null);
  const paneAfterRef = useRef<SplitPaneHandle | null>(null);

  const childArray = Array.isArray(children) ? children : children ? [children] : [];
  const clonedChildren = childArray.map((child, index) => {
    if (!isValidElement(child)) return <React.Fragment key={index}>{child}</React.Fragment>;
    if (child.type === SplitPane) {
      const ref = index === 0 ? paneBeforeRef : paneAfterRef;
      const leftPct = index === 0 ? initialLeftPercent : 100 - initialLeftPercent;
      const minPct = index === 0 ? minPercent : 100 - maxPercent;
      const maxPct = index === 0 ? maxPercent : 100 - minPercent;
      return cloneElement(
        child as React.ReactElement<{
          ref: React.RefObject<SplitPaneHandle | null>;
          initialWidth: string;
          minWidth: string;
          maxWidth: string;
        }>,
        {
          key: index === 0 ? 'pane-before' : 'pane-after',
          ref,
          initialWidth: `${leftPct}%`,
          minWidth: `${minPct}%`,
          maxWidth: `${maxPct}%`,
        }
      );
    }
    if (child.type === SplitResizer) {
      return cloneElement(
        child as React.ReactElement<{
          __beforeRef: React.RefObject<SplitPaneHandle | null>;
          __afterRef: React.RefObject<SplitPaneHandle | null>;
          __onSaveLeftPercent: (percent: number) => void;
        }>,
        {
          key: 'resizer',
          __beforeRef: paneBeforeRef,
          __afterRef: paneAfterRef,
          __onSaveLeftPercent: saveLeftPercent,
        }
      );
    }
    return <React.Fragment key={index}>{child}</React.Fragment>;
  });

  return (
    <Box
      className={className}
      h='100%'
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        minHeight: 0,
        ...style,
      }}
    >
      {clonedChildren}
    </Box>
  );
}

Split.Pane = SplitPane;
Split.Resizer = SplitResizer;
